<script lang="ts">
import { defineComponent, computed, type PropType } from 'vue'
import { type Step2TabType } from '@/types/ui/inspection/tabsTypes'
import { STEP2_TAB } from '@/constants/ui/inspection/step2/tabs'
import {
  STEP2_INSPECTION_ITEMS,
  STEP2_INSPECTION_NUMBERS
} from '@/constants/ui/inspection/step2/inspectionData'
import InspectionStep2StructureViewer from '@/components/inspection/step2/InspectionStep2StructureViewer.vue'
import type {
  BuildingStructureInspectionItem,
  Step2TypeKeys
} from '@/components/inspection/step2/InspectionStep2StructureViewer.vue'
import { useInspectionResultStore } from '@/stores/inspectionResultUpsertStore'
import type { InputSelectOption } from '@/types/ui/inspection/common/inspectionSelectOptions'

export default defineComponent({
  name: 'InspectionStep2StructureViewer',
  components: {
    InspectionStep2StructureViewer
  },
  props: {
    inspectionType: {
      type: String as PropType<Step2TabType>,
      required: true
    },
    triggerCheckNeighboring: {
      type: Boolean,
      default: false
    },
    triggerCheckFalling: {
      type: Boolean,
      default: false
    },
    saveToStoreFlag: {
      type: Boolean,
      default: false
    }
  },
  emit: ['onNeighboringCheckResult', 'onFallingCheckResult', 'savedInspectionResultStore'],
  setup(props, { emit }) {
    const inspectionResultStore = useInspectionResultStore()

    const structure = computed<BuildingStructureInspectionItem>(() => {
      const generator =
        props.inspectionType === STEP2_TAB.neighboring
          ? STEP2_INSPECTION_ITEMS.neighboring
          : STEP2_INSPECTION_ITEMS.falling

      const items = generator(inspectionResultStore.assessmentInfo.constructionType!)
      if (items === null) {
        throw new Error()
      }

      const titleNumberGenerator =
        props.inspectionType === STEP2_TAB.neighboring
          ? STEP2_INSPECTION_NUMBERS.neighboring
          : STEP2_INSPECTION_NUMBERS.falling

      const numbers = titleNumberGenerator(inspectionResultStore.assessmentInfo.constructionType!)
      if (numbers === null) {
        throw new Error()
      }

      const temp = {
        type: inspectionResultStore.assessmentInfo.constructionType!,
        items: items.map(item => ({
          ...item,
          title: `${numbers[item.key as keyof typeof numbers] ?? ''} ${item.title}`,
          inputSelectOption: item.inputSelectOption as InputSelectOption[],
          key: item.key as Step2TypeKeys
        }))
      }
      return temp
    })

    /** 入力チェック実行フラグ */
    const triggerCheckFlag = computed(() => {
      return props.inspectionType === STEP2_TAB.neighboring
        ? props.triggerCheckNeighboring
        : props.triggerCheckFalling
    })

    /**
     * 隣接建築物・周辺地盤等及び構造躯体タブの入力チェック結果受け取り処理
     * @param result 入力チェック結果（未入力リスト）
     * @returns なし
     */
    const onNeighboringCheckResult = (result: string[]): void => {
      emit('onNeighboringCheckResult', result)
    }

    /**
     * 落下危険物・店頭危険物タブの入力チェック結果受け取り処理
     * @param result 入力チェック結果（未入力リスト）
     * @returns なし
     */
    const onFallingCheckResult = (result: string[]): void => {
      emit('onFallingCheckResult', result)
    }

    /**
     * 調査結果Storeへの保存が実行されたことを親コンポーネントに通知
     */
    const savedInspectionResultStore = (): void => {
      emit('savedInspectionResultStore')
    }

    return {
      structure,
      STEP2_TAB,
      triggerCheckFlag,
      onNeighboringCheckResult,
      onFallingCheckResult,
      savedInspectionResultStore
    }
  }
})
</script>

<template>
  <div class="mb-4">
    <InspectionStep2StructureViewer
      :buildingInspectionData="structure"
      :inspectionType="inspectionType"
      :triggerCheckFlag="triggerCheckFlag"
      :saveToStoreFlag="saveToStoreFlag"
      @onNeighboringCheckResult="onNeighboringCheckResult"
      @onFallingCheckResult="onFallingCheckResult"
      @savedInspectionResultStore="savedInspectionResultStore"
    />
  </div>
</template>
